import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import {
  DrugSubstanceDose,
  DrugSubstanceInfo,
  PagedDrugSubstanceDoseResponse,
} from '../../types/DrugSubstanceDose.type';

interface DrugSubstanceDosesContextType {
  // Cached doses by cache key (substanceId or substanceId-treatmentId)
  dosesByCacheKey: Map<string, DrugSubstanceDose[]>;
  // Cached substance info by substance ID
  substanceInfoById: Map<number, DrugSubstanceInfo>;
  // Loading states
  loadingDoses: Set<string>;
  loadingInfo: Set<number>;
  // Error state
  error: string | null;

  // Methods
  fetchDrugSubstanceDoses: (
    substanceId: number,
    treatmentId?: number
  ) => Promise<DrugSubstanceDose[]>;
  fetchDrugSubstanceInfo: (
    substanceId: number
  ) => Promise<DrugSubstanceInfo | null>;
  getDrugSubstanceDoses: (
    substanceId: number,
    treatmentId?: number
  ) => DrugSubstanceDose[] | null;
  getDrugSubstanceInfo: (substanceId: number) => DrugSubstanceInfo | null;
  isLoadingDoses: (substanceId: number, treatmentId?: number) => boolean;
  isLoadingInfo: (substanceId: number) => boolean;
  clearError: () => void;
}

const DrugSubstanceDosesContext = createContext<
  DrugSubstanceDosesContextType | undefined
>(undefined);

interface DrugSubstanceDosesProviderProps {
  children: ReactNode;
}

export const DrugSubstanceDosesProvider: React.FC<
  DrugSubstanceDosesProviderProps
> = ({ children }) => {
  // Cache key format: "substanceId" or "substanceId-treatmentId"
  const [dosesByCacheKey, setDosesByCacheKey] = useState<
    Map<string, DrugSubstanceDose[]>
  >(new Map());
  const [substanceInfoById, setSubstanceInfoById] = useState<
    Map<number, DrugSubstanceInfo>
  >(new Map());
  const [loadingDoses, setLoadingDoses] = useState<Set<string>>(new Set());
  const [loadingInfo, setLoadingInfo] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // Track in-flight and failed requests with refs to avoid re-render loops
  const inFlightDoses = useRef<Set<string>>(new Set());
  const inFlightInfo = useRef<Set<number>>(new Set());
  const failedDoses = useRef<Set<string>>(new Set());
  const failedInfo = useRef<Set<number>>(new Set());

  const api = useMemo(() => gekkoApi, []);

  // Generate cache key for doses
  const getCacheKey = useCallback(
    (substanceId: number, treatmentId?: number): string => {
      return treatmentId ? `${substanceId}-${treatmentId}` : `${substanceId}`;
    },
    []
  );

  // Fetch drug substance doses
  // If treatmentId is provided, the API will filter to only doses linked to that treatment
  const fetchDrugSubstanceDoses = useCallback(
    async (
      substanceId: number,
      treatmentId?: number
    ): Promise<DrugSubstanceDose[]> => {
      const cacheKey = getCacheKey(substanceId, treatmentId);

      // Return cached if available
      if (dosesByCacheKey.has(cacheKey)) {
        return dosesByCacheKey.get(cacheKey) || [];
      }

      // Don't fetch if already in-flight or previously failed
      if (
        inFlightDoses.current.has(cacheKey) ||
        failedDoses.current.has(cacheKey)
      ) {
        return [];
      }

      try {
        inFlightDoses.current.add(cacheKey);
        setLoadingDoses((prev) => new Set(prev).add(cacheKey));
        setError(null);

        // Build URL with optional treatment_id filter
        let url = `/drug-substances/${substanceId}/doses`;
        if (treatmentId) {
          url += `?treatment_id=${treatmentId}`;
        }

        const response = await api.get<PagedDrugSubstanceDoseResponse>(url);

        const doses = response.items || [];

        setDosesByCacheKey((prev) => {
          const newMap = new Map(prev);
          newMap.set(cacheKey, doses);
          return newMap;
        });

        return doses;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch doses';
        failedDoses.current.add(cacheKey);
        setError(errorMessage);
        console.error('Error fetching drug substance doses:', err);
        return [];
      } finally {
        inFlightDoses.current.delete(cacheKey);
        setLoadingDoses((prev) => {
          const newSet = new Set(prev);
          newSet.delete(cacheKey);
          return newSet;
        });
      }
    },
    [api, dosesByCacheKey, getCacheKey]
  );

  // Fetch drug substance info
  const fetchDrugSubstanceInfo = useCallback(
    async (substanceId: number): Promise<DrugSubstanceInfo | null> => {
      // Return cached if available
      if (substanceInfoById.has(substanceId)) {
        return substanceInfoById.get(substanceId) || null;
      }

      // Don't fetch if already in-flight or previously failed
      if (
        inFlightInfo.current.has(substanceId) ||
        failedInfo.current.has(substanceId)
      ) {
        return null;
      }

      try {
        inFlightInfo.current.add(substanceId);
        setLoadingInfo((prev) => new Set(prev).add(substanceId));
        setError(null);

        const response = await api.get<DrugSubstanceInfo>(
          `/drug-substances/${substanceId}`
        );

        setSubstanceInfoById((prev) => {
          const newMap = new Map(prev);
          newMap.set(substanceId, response);
          return newMap;
        });

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch substance info';
        failedInfo.current.add(substanceId);
        setError(errorMessage);
        console.error('Error fetching drug substance info:', err);
        return null;
      } finally {
        inFlightInfo.current.delete(substanceId);
        setLoadingInfo((prev) => {
          const newSet = new Set(prev);
          newSet.delete(substanceId);
          return newSet;
        });
      }
    },
    [api, substanceInfoById]
  );

  // Get cached doses
  const getDrugSubstanceDoses = useCallback(
    (substanceId: number, treatmentId?: number): DrugSubstanceDose[] | null => {
      const cacheKey = getCacheKey(substanceId, treatmentId);
      return dosesByCacheKey.get(cacheKey) || null;
    },
    [dosesByCacheKey, getCacheKey]
  );

  // Get cached substance info
  const getDrugSubstanceInfo = useCallback(
    (substanceId: number): DrugSubstanceInfo | null => {
      return substanceInfoById.get(substanceId) || null;
    },
    [substanceInfoById]
  );

  // Check if loading doses
  const isLoadingDoses = useCallback(
    (substanceId: number, treatmentId?: number): boolean => {
      const cacheKey = getCacheKey(substanceId, treatmentId);
      return loadingDoses.has(cacheKey);
    },
    [loadingDoses, getCacheKey]
  );

  // Check if loading info
  const isLoadingInfo = useCallback(
    (substanceId: number): boolean => {
      return loadingInfo.has(substanceId);
    },
    [loadingInfo]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: DrugSubstanceDosesContextType = {
    dosesByCacheKey,
    substanceInfoById,
    loadingDoses,
    loadingInfo,
    error,
    fetchDrugSubstanceDoses,
    fetchDrugSubstanceInfo,
    getDrugSubstanceDoses,
    getDrugSubstanceInfo,
    isLoadingDoses,
    isLoadingInfo,
    clearError,
  };

  return (
    <DrugSubstanceDosesContext.Provider value={value}>
      {children}
    </DrugSubstanceDosesContext.Provider>
  );
};

export const useDrugSubstanceDoses = (): DrugSubstanceDosesContextType => {
  const context = useContext(DrugSubstanceDosesContext);
  if (!context) {
    throw new Error(
      'useDrugSubstanceDoses must be used within a DrugSubstanceDosesProvider'
    );
  }
  return context;
};

export default DrugSubstanceDosesProvider;
